import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ResponseModeBadge } from '../components/dashboard/StatusBadge';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Activity, Clock, TrendingUp, Database, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart style constants - prevents re-creation on every render
const CHART_STYLES = {
  tooltip: {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  },
  xAxisTick: { fill: 'hsl(var(--muted-foreground))' },
  yAxisTick: { fill: 'hsl(var(--muted-foreground))' },
  yAxisLabel: { 
    value: 'ms', 
    angle: -90, 
    position: 'insideLeft', 
    fill: 'hsl(var(--muted-foreground))' 
  },
};

const Monitoring = () => {
  const [metrics, setMetrics] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMonitoringData = React.useCallback(async () => {
    try {
      const [metricsRes, requestsRes, healthRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/metrics`),
        fetch(`${API_URL}/api/analytics/requests?limit=10&sort_by=timestamp&sort_order=desc`),
        fetch(`${API_URL}/api/analytics/health`)
      ]);

      const metricsData = await metricsRes.json();
      const requestsData = await requestsRes.json();
      const healthData = await healthRes.json();

      setMetrics(metricsData);
      setRecentRequests(requestsData.requests || []);
      setHealth(healthData);
    } catch (error) {
      // Error logging - production should use proper error service
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching monitoring data:', error);
      }
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitoringData();
    
    // Auto-refresh every 10 seconds for monitoring
    const interval = setInterval(fetchMonitoringData, 10000);
    return () => clearInterval(interval);
  }, [fetchMonitoringData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMonitoringData();
    toast.success('Monitoring data refreshed');
  };

  // Mock latency data for chart (would need time-series endpoint for real data)
  const latencyData = useMemo(() => {
    if (!metrics) return [];
    const now = Date.now();
    return Array.from({ length: 12 }, (_, i) => ({
      hour: format(new Date(now - (12 - i) * 3600000), 'HH:mm'),
      avg: metrics.avgLatency + Math.random() * 200 - 100,
      p95: metrics.p95Latency + Math.random() * 300 - 150,
    }));
  }, [metrics]);

  const throughputData = useMemo(() => {
    if (!metrics) return [];
    const now = Date.now();
    return Array.from({ length: 12 }, (_, i) => ({
      hour: format(new Date(now - (12 - i) * 3600000), 'HH:mm'),
      requests: Math.round(metrics.totalRequests / 24) + Math.random() * 20 - 10,
    }));
  }, [metrics]);

  if (loading || !metrics) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading monitoring data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Monitoring</h1>
          <p className="text-muted-foreground mt-1">
            Real-time system performance and request tracking
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* System Health Status */}
      {health && (
        <Card className={`border-${health.status === 'healthy' ? 'success' : 'warning'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-${health.status === 'healthy' ? 'success' : 'warning'} rounded-lg`}>
                <Activity className={`h-5 w-5 text-${health.status === 'healthy' ? 'success' : 'warning'}-foreground`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">{health.message}</h3>
                <p className="text-sm text-muted-foreground">
                  {health.recentRequests} requests in last 5 minutes • {health.recentErrors} errors
                </p>
              </div>
              <Badge variant="outline">
                Status: {health.status}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Real-time Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Requests/min</p>
                <p className="text-2xl font-bold">{Math.round(metrics.totalRequests / 1440)}</p>
              </div>
              <Activity className="h-8 w-8 text-chart-1" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Latency</p>
                <p className="text-2xl font-bold">{metrics.avgLatency}ms</p>
              </div>
              <Clock className="h-8 w-8 text-chart-2" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">P95 Latency</p>
                <p className="text-2xl font-bold">{metrics.p95Latency}ms</p>
              </div>
              <TrendingUp className="h-8 w-8 text-chart-3" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cache Hit</p>
                <p className="text-2xl font-bold">{metrics.cacheHitRate}%</p>
              </div>
              <Database className="h-8 w-8 text-chart-4" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Latency Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Latency Trends</CardTitle>
            <CardDescription>Response time percentiles over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={latencyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis 
                  dataKey="hour" 
                  tick={CHART_STYLES.xAxisTick}
                />
                <YAxis 
                  label={CHART_STYLES.yAxisLabel}
                  tick={CHART_STYLES.yAxisTick}
                />
                <Tooltip contentStyle={CHART_STYLES.tooltip} />
                <Line 
                  type="monotone" 
                  dataKey="avg" 
                  stroke="hsl(var(--chart-1))" 
                  strokeWidth={2}
                  name="Average"
                />
                <Line 
                  type="monotone" 
                  dataKey="p95" 
                  stroke="hsl(var(--chart-4))" 
                  strokeWidth={2}
                  name="P95"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Throughput */}
        <Card>
          <CardHeader>
            <CardTitle>Request Throughput</CardTitle>
            <CardDescription>Total requests per hour</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={throughputData}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis 
                  dataKey="hour" 
                  tick={CHART_STYLES.xAxisTick}
                />
                <YAxis 
                  tick={CHART_STYLES.yAxisTick}
                />
                <Tooltip contentStyle={CHART_STYLES.tooltip} />
                <Area 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="hsl(var(--chart-1))" 
                  fillOpacity={1}
                  fill="url(#colorRequests)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Live Request Stream */}
      <Card>
        <CardHeader>
          <CardTitle>Live Request Stream</CardTitle>
          <CardDescription>Real-time request monitoring</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trace ID</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Cache</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentRequests.map((req) => (
                  <TableRow key={req.trace_id}>
                    <TableCell className="font-mono text-xs">
                      {req.trace_id.substring(0, 8)}...
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(req.timestamp), 'HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{req.destination.city}</div>
                      <div className="text-xs text-muted-foreground">{req.destination.country}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={req.status_code === 200 ? "outline" : "destructive"}>
                        {req.response_mode}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{req.provider_used || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <span className={req.latency_ms > 2000 ? 'text-destructive' : req.latency_ms > 1000 ? 'text-warning' : 'text-success'}>
                        {req.latency_ms}ms
                      </span>
                    </TableCell>
                    <TableCell>
                      {req.cache_hit ? (
                        <Badge variant="outline" className="bg-success-light text-success border-success/20 text-xs">
                          Hit
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          Miss
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {req.calculated_rates && req.calculated_rates[0] 
                        ? `${(parseInt(req.calculated_rates[0].total_price) / 100).toFixed(0)} SAR`
                        : 'N/A'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Monitoring;
